/* eslint-disable no-fallthrough */
import { ApplicantStatus, ApplicationStatus } from "shared/types";
import { applicationStatusTitle } from "./title";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { ReactNode, useMemo } from "react";
import { cx } from "shared/utils/classnames";
import {
  Loading03Icon,
  MinusSignIcon,
  PauseCircleIcon,
  Tick01Icon
} from "@hugeicons-pro/core-solid-rounded";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

interface ApplicationStatusBadge {
  status?: ApplicantStatus | ApplicationStatus;
  isAutoCanceled?: boolean;
  size?: BadgeSize;
  className?: string;

  // since they both application types use same interface on
  // backend -- we need to diversify statuses.

  // there are some differences on entity level of understanding

  // for applicant status is completed past submitted state
  // also we're not interested in decision on applicant basis

  // for application Not Started and Pending ApplicationStatus translates to Invited.

  // yet another way to put it:
  // when displaying data.application.status -- type: "application"
  // when displaying data.application.applicant[n].["status" | "application_status"] -- type: "applicant"

  // applicant -- status on applicant
  // application --  status of whole application
  type: "applicant" | "application";

  conditions?: string[];
  autoApproved?: boolean;
}

const applicationConditionsMap = {
  inadequate_income: "Inadequate income assets",
  unverified_income: "Unverified income assets",
  income_documents_fraud_risk_detected: "Income documents fraud risk detected",
  smoking_habits: "Smoking habits",
  criminal_record_history: "Criminal record history",
  non_permitted_pets: "Non-permitted pets",
  exceeded_pet_limit: "Exceeded pet limit",
  rental_history: "Rental history is lacking",
  history_of_non_payment_or_tardiness: "History of non-payment or tardiness",
  history_of_rental_property_damage: "History of rental property damage",
  application_info_misrepresented: "Application info misrepresented",
  incomplete_application: "Incomplete application",
  credit_background_check_not_authorized:
    "Credit/background check not authorized",
  credit_score_doesnt_meet_renting_criteria:
    "Credit score(s) of application group doesn’t meet renting criteria",
  history_of_bankruptcy: "History of bankruptcy",
  history_of_eviction: "History of housing court proceedings",
  negative_employer_reference: "Negative employer reference",
  disagreement_on_terms_of_rental_agreement:
    "Disagreement on terms of rental agreement",
  registered_sex_offender_record: "Registered sex offender record",
  past_due_accounts: "Past due accounts",
  accounts_in_collections: "Accounts in collections",

  //WA adverse action letter type conditions
  increased_deposit: "Residency requires an increased deposit",
  qualified_guarantors: "Residency requires a qualified guarantor",
  last_months_rent: "Residency requires last month's rent",
  increased_monthly_rent: "Residency requires an increased monthly rent",
  information_contained_in_a_consumer_report:
    "Information contained in a consumer report",
  the_consumer_credit_report_did_not_contain_sufficient_information:
    "The consumer credit report did not contain sufficient information",
  information_received_from_previous_rental_history_or_reference:
    "Information received from previous rental history or reference",
  information_received_in_a_criminal_record:
    "Information received in a criminal record",
  information_received_in_a_civil_record:
    "Information received in a civil record",
  information_received_from_an_employment_verification:
    "Information received from an employment verification"
};

export const ApplicationStatusBadge = ({
  status,
  isAutoCanceled,
  type,
  size = BadgeSize.SM,
  className,
  conditions,
  autoApproved
}: ApplicationStatusBadge) => {
  const {
    variant,
    title,
    tooltip,
    hugeIcon
  }: {
    variant: BadgeVariant;
    hugeIcon?: ReactNode;
    title: string;
    tooltip?: ReactNode;
  } = useMemo(() => {
    if (!status)
      return {
        variant: BadgeVariant.BASE,
        title: "Invited"
      };

    if (type === "applicant") {
      if (status === ApplicantStatus.Canceled && isAutoCanceled)
        return {
          variant: BadgeVariant.BASE,
          title: "Auto-canceled"
        };
      switch (status) {
        case ApplicantStatus.Pending:
          return { variant: BadgeVariant.BASE, title: "Invited" };
        case ApplicantStatus.Finished:
        case ApplicantStatus.Started:
          return {
            variant: BadgeVariant.WARNING,
            title: "In progress"
          };
        case ApplicantStatus.Submitted:
          return {
            variant: BadgeVariant.SUCCESS,
            title: "Submitted"
          };
        case ApplicantStatus.Canceled:
          return {
            variant: BadgeVariant.BASE,
            title: "Canceled"
          };
        case ApplicantStatus.Processing:
          return {
            variant: BadgeVariant.WARNING,
            title: "Waiting for reports"
          };
        case ApplicantStatus.OnHold:
          return {
            hugeIcon: (
              <HugeiconsIcon
                icon={PauseCircleIcon}
                size={16}
                className="text-content-500"
              />
            ),
            variant: BadgeVariant.BASE,
            title: "On hold"
          };

        default:
          // Should be impossible case
          return {
            variant: BadgeVariant.BASE,
            title: status
          };
      }
    }

    if (status === ApplicationStatus.Canceled && isAutoCanceled)
      return {
        variant: BadgeVariant.BASE,
        title: applicationStatusTitle({ status, isAutoCanceled, autoApproved }),
        hugeIcon: <HugeiconsIcon icon={Cancel01Icon} size={12} />
      };
    if (status === ApplicationStatus.Approved && autoApproved)
      return {
        variant: BadgeVariant.SUCCESS,
        title: applicationStatusTitle({ status, isAutoCanceled, autoApproved }),
        hugeIcon: <HugeiconsIcon icon={Tick01Icon} size={12} />
      };

    switch (status) {
      case ApplicationStatus.Started:
      case ApplicationStatus.PartiallySubmitted:
        return {
          variant: BadgeVariant.WARNING,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          hugeIcon: <HugeiconsIcon icon={Loading03Icon} size={12} />
        };

      case ApplicationStatus.Pending:
        return {
          variant: BadgeVariant.BASE,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          hugeIcon: <HugeiconsIcon icon={MinusSignIcon} size={12} />
        };

      case ApplicationStatus.Submitted:
        return {
          variant: BadgeVariant.PRIMARY,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          hugeIcon: <HugeiconsIcon icon={Tick01Icon} size={12} />
        };

      case ApplicationStatus.Canceled:
        return {
          variant: BadgeVariant.BASE,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          hugeIcon: <HugeiconsIcon icon={Cancel01Icon} size={12} />
        };

      case ApplicationStatus.Approved:
        return {
          variant: BadgeVariant.SUCCESS,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          hugeIcon: <HugeiconsIcon icon={Tick01Icon} size={12} />
        };

      case ApplicationStatus.ConditionalApproved:
        return {
          hugeIcon: <HugeiconsIcon icon={Tick01Icon} size={12} />,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          tooltip: (
            <>
              Application was conditionally approved
              {conditions?.length ? (
                <ul>
                  {conditions.map((c) => (
                    <li key={c} className="my-1 ml-5 list-disc text-xs">
                      {applicationConditionsMap[c] || c}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ),
          variant: BadgeVariant.SUCCESS
        };

      case ApplicationStatus.Rejected:
        return {
          variant: BadgeVariant.ERROR,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          hugeIcon: <HugeiconsIcon icon={Cancel01Icon} size={12} />
        };
      case ApplicationStatus.OnHold:
        return {
          variant: BadgeVariant.BASE,
          title: applicationStatusTitle({
            status,
            isAutoCanceled,
            autoApproved
          }),
          hugeIcon: (
            <HugeiconsIcon
              icon={PauseCircleIcon}
              size={16}
              className="text-content-500"
            />
          )
        };
      default:
        return { variant: BadgeVariant.BASE, title: status };
    }
  }, [status, type]);

  return (
    <Badge
      className={cx("shrink-0 items-center", className)}
      size={size}
      variant={variant}
      tooltip={tooltip}
      leftElement={hugeIcon}
      tooltipClassName="z-[11]"
    >
      {title}
    </Badge>
  );
};
